<template>
  <div class="history-panel" 
       :class="{ 'show': show, 'pointer-events-none': !show }">
    <div class="flex items-center justify-between !bg-secondary px-4 rounded-t-lg">
      <div class="!text-white flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        {{ activeHistoryType === 'prompts' ? 'Prompt History' : 'Content History' }}
      </div>
      <div class="history-controls">
        <!-- Toggle switch between prompt and content history -->
        <button 
          class="history-toggle-btn tooltip tooltip-left" 
          @click.prevent="toggleHistoryType"
          :data-tip="activeHistoryType === 'prompts' ? 'Content' : 'Prompt'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
          </svg>
        </button>
        <a href="#" class="close-history" @click.prevent="$emit('close')">×</a>
      </div>
    </div>
    <div class="history-list">
      <div v-if="displayHistory.length === 0" class="empty-history">
        <p v-if="activeHistoryType === 'prompts'">No prompt history yet. Generate content to save prompts here.</p>
        <p v-else>No content history yet. Generated content will appear here.</p>
      </div>
      <div 
        class="history-item" 
        v-for="(item, index) in displayHistory" 
        :key="index" 
        @click="selectHistoryItem(item)"
      >
        <p>{{ item.text.substring(0, 100) }}{{ item.text.length > 100 ? '...' : '' }}</p>
        <span class="history-item-date">{{ formatDate(item.timestamp) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HistoryPanel',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    promptHistory: {
      type: Array,
      default: () => []
    },
    contentHistory: {
      type: Array,
      default: () => []
    },
    historyType: {
      type: String,
      default: 'prompts'
    }
  },
  data() {
    return {
      activeHistoryType: this.historyType
    };
  },
  computed: {
    displayHistory() {
      return this.activeHistoryType === 'prompts' ? this.promptHistory : this.contentHistory;
    }
  },
  watch: {
    historyType(newValue) {
      this.activeHistoryType = newValue;
    }
  },
  methods: {
    selectHistoryItem(item) {
      this.$emit('select-history-item', item.text, this.activeHistoryType);
    },
    formatDate(timestamp) {
      if (!timestamp) return 'Unknown';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hours ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} days ago`;
      
      return date.toLocaleDateString();
    },
    toggleHistoryType() {
      this.activeHistoryType = this.activeHistoryType === 'prompts' ? 'content' : 'prompts';
      this.$emit('toggle-history-type', this.activeHistoryType);
    }
  }
}
</script>

<style scoped>
.history-panel {
  position: absolute;
  top: 0;
  left: 200px;
  width: 300px;
  height: 100%;
  background: #fff;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out, opacity 0.1s ease-in-out;
  border-radius: 8px;
  overflow: hidden;
  z-index: -1;
  opacity: 0;
  transform: translateX(-100%);
}

.history-panel.show {
  transform: translateX(-505px);
  opacity: 1;
}

.history-header {
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(45deg, #0a0909, #3d0418) !important;
  position: sticky;
  top: 0;
  z-index: 1;
}

.history-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.history-toggle-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.history-toggle-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.history-header h3 {
  margin: 0;
  font-size: 16px;
  color: #fff;
}

.close-history {
  font-size: 20px;
  color: #fff;
  text-decoration: none;
  padding: 5px;
}

.history-list {
  padding: 10px;
  height: calc(100% - 50px);
  overflow-y: auto;
}

.history-item {
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.history-item:hover {
  background-color: #f8f8f8;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.history-item p {
  margin: 0;
  font-size: 12px;
  color: #666;
  white-space: pre-wrap;
  word-break: break-word;
}

.history-item-date {
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 10px;
  color: #999;
  background: rgba(255,255,255,0.8);
  padding: 2px 5px;
  border-radius: 3px;
}

.empty-history {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: #999;
  font-style: italic;
}

/* Custom scrollbar for history list */
.history-list::-webkit-scrollbar {
  width: 8px;
  background: #f1f1f1;
}

.history-list::-webkit-scrollbar-thumb {
  background: pink;
  border-radius: 10px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: pink;
}

.pointer-events-none {
  pointer-events: none;
}
</style> 